import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, MessageCircle, Clock, Sparkles, Shield, Zap, Building2, Headphones, GraduationCap, ShoppingBag, Play, Check, SparklesIcon } from 'lucide-react';

const LandingPage: React.FC = () => {
  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Brain',
      description: "Powered by Convai's advanced AI, Diva understands context, answers questions, and learns from interactions.",
      bgColor: 'bg-red-500/10',
      iconColor: 'text-red-500',
    },
    {
      icon: MessageCircle,
      title: 'Natural Conversations',
      description: 'Diva speaks naturally with human-like expressions and gestures, creating genuine connections with your audience.',
      bgColor: 'bg-red-600/10',
      iconColor: 'text-red-600',
    },
    {
      icon: Clock,
      title: 'Always Available',
      description: 'No sick days, no vacations. Diva is available 24/7 to represent your brand and answer customer questions.',
      bgColor: 'bg-red-500/10',
      iconColor: 'text-red-500',
    },
    {
      icon: Sparkles,
      title: 'Stunning Visuals',
      description: 'Built with Unreal Engine, Diva delivers cinema-quality visuals that captivate and engage your audience.',
      bgColor: 'bg-red-600/10',
      iconColor: 'text-red-600',
    },
    {
      icon: Shield,
      title: 'Always On-Brand',
      description: 'Diva stays perfectly on-message. Train her once, and she delivers consistent brand representation every time.',
      bgColor: 'bg-red-500/10',
      iconColor: 'text-red-500',
    },
    {
      icon: Zap,
      title: 'Instant Deployment',
      description: 'Get started in minutes. No complex setup or lengthy training required — Diva is ready to work immediately.',
      bgColor: 'bg-red-600/10',
      iconColor: 'text-red-600',
    },
  ];

  const useCases = [
    {
      icon: Building2,
      title: 'Corporate Reception',
      description: 'Diva greets visitors, answers company questions, and directs them to the right department — creating a memorable first impression.',
      image: '/images/reception.png',
    },
    {
      icon: Headphones,
      title: 'Customer Support',
      description: 'Provide instant, friendly support around the clock. Diva handles common inquiries while escalating complex issues to your team.',
      image: '/images/support.png',
    },
    {
      icon: GraduationCap,
      title: 'Education & Training',
      description: 'Use Diva as an interactive tutor or training assistant. She can explain concepts, answer questions, and guide learners.',
      image: '/images/education.png',
    },
    {
      icon: ShoppingBag,
      title: 'Retail & E-commerce',
      description: 'Diva helps customers find products, answers questions about inventory, and provides personalized shopping assistance.',
      image: '/images/retail.png',
    },
  ];

  return (
    <main className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28 min-h-screen flex items-center">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-black"></div>
          {/* Video background for desktop */}
          <div className="absolute w-[150%] h-[150%] -left-[25%] -top-[25%] hidden md:block video-container">
            <iframe style={{ border: 'none' }} className="w-full h-full" src="https://www.youtube.com/embed/gKeWJPXAJBM?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playlist=gKeWJPXAJBM" title="Discover DIVA" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
          </div>
          {/* Lava blobs for mobile */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900 md:hidden"></div>
          <div className="lava-blob blob-1 md:hidden"></div>
          <div className="lava-blob blob-2 md:hidden"></div>
          <div className="lava-blob blob-3 md:hidden"></div>
          <div className="lava-blob blob-4 md:hidden"></div>
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 via-black/70 to-gray-900/80"></div>
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 backdrop-blur-sm" style={{ backgroundColor: 'rgba(211, 47, 47, 0.2)', color: '#FF5252' }}>
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
              Powered by Convai & Unreal Engine
            </div>

            {/* Heading */}
            <h1 className="text-4xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Your Brand's<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800">
                First AI Hire
              </span>
            </h1>

            {/* Description */}
            <p className="text-lg lg:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Meet Diva — your AI-powered digital human brand ambassador. Available 24/7, always on-brand, and ready to represent your business.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href="mailto:lets@discoverdiva.ai"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-red-600 hover:bg-red-700 rounded-full shadow-lg shadow-red-600/25 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Hire Diva Today
              </a>
              <Link
                to="/demo"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-medium text-white bg-gray-800/80 backdrop-blur border-2 border-gray-600 rounded-full hover:bg-gray-700 hover:scale-105 transition-all duration-200"
              >
                <SparklesIcon className="w-5 h-5" />
                Try Demo
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex items-center gap-6 justify-center text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-red-500" />
                <span className="hidden sm:inline text-gray-300">24/7 Availability</span>
              </div>
              <span className="text-gray-600">|</span>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-red-500" />
                <span className="hidden sm:inline text-gray-300">No Training Needed</span>
              </div>
              <span className="text-gray-600">|</span>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-red-500" />
                <span className="hidden sm:inline text-gray-300">Always On-Brand</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-28 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: 'rgba(211, 47, 47, 0.2)', color: '#FF5252' }}>
              Why Choose Diva
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
              Your Brand Ambassador{' '}
              <span style={{ color: '#FF5252' }}>Powered by AI</span>
            </h2>
            <p className="text-lg text-gray-300">
              Diva combines cutting-edge AI technology with stunning visuals to create the perfect digital representative for your brand.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-8 rounded-2xl bg-gray-800 hover:bg-gray-750 border border-gray-700 hover:border-gray-600 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 ${feature.bgColor}`}>
                  <feature.icon className={`w-7 h-7 ${feature.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 lg:py-28 bg-black relative overflow-hidden">
        {/* Diva Image */}
        <div className="absolute bottom-0 right-0 w-[32rem] h-[32rem] lg:w-[48rem] lg:h-[48rem] pointer-events-none">
          <img
            src="/images/diva1.png"
            alt="Diva"
            className="w-full h-full object-contain object-right-bottom"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: 'rgba(211, 47, 47, 0.2)', color: '#FF5252' }}>
              Use Cases
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
              Where Can Diva{' '}
              <span style={{ color: '#FF5252' }}>Work for You?</span>
            </h2>
            <p className="text-lg text-gray-300">
              From reception desks to websites, Diva adapts to your needs and represents your brand wherever your customers are.
            </p>
          </div>

          {/* Use Cases Grid */}
          <div className="grid lg:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="group bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-700"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-500/90">
                      <useCase.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{useCase.title}</h3>
                  <p className="text-gray-300">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #D32F2F, #B71C1C)' }}>
        {/* Animated blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="cta-lava-blob cta-blob-1"></div>
          <div className="cta-lava-blob cta-blob-2"></div>
          <div className="cta-lava-blob cta-blob-3"></div>
          <div className="cta-lava-blob cta-blob-4"></div>
          <div className="cta-lava-blob cta-blob-5"></div>
        </div>

        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center z-10">
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
            Ready to Meet Your New AI Ambassador?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join forward-thinking brands using AI digital humans to transform customer engagement and create unforgettable experiences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:lets@discoverdiva.ai"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-red-700 bg-white rounded-full shadow-lg hover:bg-gray-100 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Hire Diva Today
            </a>
            <Link
              to="/demo"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-medium text-white bg-transparent border-2 border-white rounded-full hover:bg-white/10 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              <SparklesIcon className="w-5 h-5" />
              Try Live Demo
            </Link>
          </div>
          <p className="mt-8 text-white/70 text-sm">
            No commitment required • Custom solutions available • Quick setup
          </p>
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
